import { InterviewStatus } from '@bosmart/types'

interface SessionControlsProps {
  isMuted: boolean
  isScreenSharing: boolean
  isCallEnding: boolean
  showConfirmation: boolean
  status: InterviewStatus
  onToggleMute: () => void
  onToggleScreenShare: () => void
  onEndCall: () => void
}

export function SessionControls({
  isMuted,
  isScreenSharing,
  isCallEnding,
  showConfirmation,
  status,
  onToggleMute,
  onToggleScreenShare,
  onEndCall,
}: SessionControlsProps) {
  const canEnd = status === InterviewStatus.CONNECTED

  return (
    <div className="flex items-center justify-center gap-6">
      <button
        className={`p-4 rounded-full transition-all duration-200 border transform hover:scale-105 ${isMuted ? 'bg-red-500/20 text-red-500 border-red-500/50' : 'bg-dark-800 hover:bg-dark-700 text-white border-white/10 hover:border-white/30'}`}
        onClick={onToggleMute}
        title={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <MutedIcon /> : <MicIcon />}
      </button>

      <button
        className={`p-4 rounded-full transition-all duration-200 border transform hover:scale-105 ${isScreenSharing ? 'bg-accent-500 text-black border-accent-400 shadow-lg shadow-accent-500/20' : 'bg-dark-800 hover:bg-dark-700 text-white border-white/10 hover:border-white/30'}`}
        onClick={onToggleScreenShare}
        title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
      >
        <ScreenIcon />
      </button>

      <div className="relative">
        {isCallEnding && !showConfirmation && <EndCue />}
        <button
          onClick={onEndCall}
          title="End Interview"
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 border transform hover:scale-105 ${
            canEnd
              ? 'bg-red-500 hover:bg-red-600 text-white border-red-500/50 shadow-lg shadow-red-500/30'
              : 'bg-gray-800 text-gray-500 cursor-not-allowed border-white/10'
          } ${isCallEnding && !showConfirmation ? 'animate-blink ring-4 ring-red-400 shadow-[0_0_30px_rgba(239,68,68,0.6)] scale-110' : ''}`}
          disabled={!canEnd}
        >
          <EndIcon />
          <span className="text-sm font-medium pr-2">End Call</span>
        </button>
      </div>
    </div>
  )
}

function EndCue() {
  return (
    <div className="absolute -top-16 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce z-50">
      <div className="bg-red-600 text-white text-[10px] font-bold uppercase px-3 py-1 rounded-full shadow-lg mb-1 whitespace-nowrap tracking-wider">
        Click to end
      </div>
      <svg
        className="w-8 h-8 text-red-500 drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
      </svg>
    </div>
  )
}

function MicIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  )
}

function MutedIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
        clipRule="evenodd"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
      />
    </svg>
  )
}

function ScreenIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  )
}

function EndIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}
